import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { AlertTriangle, FileText, Link2, Lock, Mail, Paperclip, Upload } from 'lucide-react';
import { MAX_UPLOAD_BYTES, type AttachmentRequest, type LibraryFile } from '@mailpilot/shared';
import { api } from '../../lib/api';
import { qk } from '../../lib/keys';
import { uploadFile } from '../../lib/upload';
import { bytes, displayName, fileKind, shortDate } from '../../lib/format';
import { Badge, Button, ErrorState, InlineError, Modal, Notice, Segmented, SkeletonLines, Spinner, useToast } from '../ui';
import { fileIcon } from '../mail/AttachmentChip';

type Source = 'library' | 'upload';
type Delivery = 'link' | 'attachment';
type Uploaded = { uploadId: string; filename: string; size: number; mimeType: string };

/** A file can be offered for a request only when it is approved, current and unexpired. */
export function isUsableLibraryFile(f: LibraryFile, now = Date.now()): boolean {
  return f.approved && !f.supersededById && (!f.expiresAt || new Date(f.expiresAt).getTime() > now);
}

/** Is this email permitted by the file's allowed recipients / domains? null = no restrictions set. */
export function recipientAllowed(f: LibraryFile, email: string | null | undefined): boolean | null {
  if (!f.allowedRecipients.length && !f.allowedDomains.length) return null;
  if (!email) return false;
  const e = email.toLowerCase();
  const domain = e.split('@')[1] ?? '';
  return (
    f.allowedRecipients.some((r) => r.toLowerCase() === e) ||
    f.allowedDomains.some((d) => {
      const dd = d.toLowerCase().replace(/^@/, '');
      return domain === dd || domain.endsWith(`.${dd}`);
    })
  );
}

export function ResolveRequestDialog({ request, open, onClose }: { request: AttachmentRequest | null; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const library = useQuery({ queryKey: qk.library, queryFn: () => api('GET /api/library'), enabled: open });
  const sendAsLinks = library.data?.settings.sendAsLinks ?? false;

  const [source, setSource] = useState<Source>('library');
  const [fileId, setFileId] = useState<string | null>(null);
  const [uploaded, setUploaded] = useState<Uploaded | null>(null);
  const [deliverAs, setDeliverAs] = useState<Delivery>('attachment');

  const usable = useMemo(() => (library.data?.files ?? []).filter((f) => isUsableLibraryFile(f)), [library.data]);

  // Reset whenever a (different) request is opened.
  useEffect(() => {
    if (!open) return;
    setUploaded(null);
    setFileId(null);
    setSource('library');
  }, [open, request?.id]);

  // Preselect the suggested file once the library has loaded.
  useEffect(() => {
    if (!open || !library.data) return;
    setFileId((cur) => {
      if (cur && usable.some((f) => f.id === cur)) return cur;
      const s = request?.suggestedLibraryFileId;
      if (s && usable.some((f) => f.id === s)) return s;
      return null;
    });
    if (!usable.length) setSource((s) => (s === 'library' ? 'upload' : s));
  }, [open, library.data, usable, request?.suggestedLibraryFileId]);

  useEffect(() => {
    if (!open || !library.data) return;
    setDeliverAs(library.data.settings.sendAsLinks ? 'link' : 'attachment');
  }, [open, library.data, request?.id]);

  const upload = useMutation({
    mutationFn: (file: File) => uploadFile(file),
    onSuccess: (r) => setUploaded(r),
    onError: (e) => toast.error(e),
  });

  const resolve = useMutation({
    mutationFn: () => {
      if (!request) throw new Error('No request selected');
      const body =
        source === 'library'
          ? { libraryFileId: fileId ?? undefined, deliverAs }
          : { uploadId: uploaded?.uploadId, deliverAs };
      return api('POST /api/attachment-requests/:id/resolve', { params: { id: request.id }, body });
    },
    onSuccess: () => {
      toast.success('File attached — the reply draft is ready for your review.');
      void qc.invalidateQueries({ queryKey: qk.attachmentRequestsRoot });
      void qc.invalidateQueries({ queryKey: ['thread'] });
      void qc.invalidateQueries({ queryKey: qk.draftsRoot });
      onClose();
    },
    onError: (e) => toast.error(e),
  });

  const selected = usable.find((f) => f.id === fileId) ?? null;
  const requesterEmail = request?.requestedBy?.email ?? null;
  const allowed = selected ? recipientAllowed(selected, requesterEmail) : null;
  const effectiveDelivery: Delivery = sendAsLinks ? deliverAs : 'attachment';
  const canSubmit = !!request && (source === 'library' ? !!selected : !!uploaded) && !upload.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Provide the requested file"
      description="The substantive reply stays pending until you choose a file. Nothing is sent from here — the file is added to the draft."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!canSubmit} loading={resolve.isPending} onClick={() => resolve.mutate()}>
            {effectiveDelivery === 'link' ? 'Add as protected link' : 'Add as attachment'}
          </Button>
        </>
      }
    >
      {!request ? (
        <p className="text-[0.8125rem] text-muted">No request selected.</p>
      ) : (
        <div className="space-y-5">
          <section aria-label="Request details" className="space-y-2 rounded-2xl border border-hairline bg-raised/50 p-4">
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-primary-soft text-primary-ink">
                <FileText className="h-4 w-4" aria-hidden />
              </span>
              <div className="min-w-0">
                <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">Requested document</p>
                <p className="font-semibold text-ink">{request.description}</p>
              </div>
            </div>
            {request.reason && (
              <p className="text-[0.8125rem] text-ink">
                <span className="font-medium text-muted">Why it's needed: </span>
                {request.reason}
              </p>
            )}
            <p className="flex flex-wrap items-center gap-1.5 text-[0.8125rem] text-ink">
              <Mail className="h-4 w-4 text-muted" aria-hidden />
              <span className="font-medium text-muted">Asked by:</span>
              {request.requestedBy ? (
                <span>
                  {displayName(request.requestedBy)}
                  {request.requestedBy.name && <span className="text-muted"> &lt;{request.requestedBy.email}&gt;</span>}
                </span>
              ) : (
                <span className="text-muted">Unknown sender</span>
              )}
              {request.threadId && (
                <Link to={`/inbox/${request.threadId}`} onClick={onClose} className="font-semibold text-primary-ink underline-offset-2 hover:underline">
                  View email
                </Link>
              )}
            </p>
          </section>

          <Segmented<Source>
            label="File source"
            value={source}
            onChange={setSource}
            options={[
              { value: 'library', label: 'Select from library' },
              { value: 'upload', label: 'Upload file' },
            ]}
          />

          {source === 'library' ? (
            <div className="space-y-3">
              {library.isLoading ? (
                <SkeletonLines lines={4} />
              ) : library.isError ? (
                <ErrorState compact error={library.error} onRetry={() => void library.refetch()} />
              ) : usable.length === 0 ? (
                <Notice tone="info">
                  No approved, current library files are available. Upload the file instead, or approve one on the{' '}
                  <Link to="/library" onClick={onClose} className="font-semibold underline">
                    Library page
                  </Link>
                  .
                </Notice>
              ) : (
                <div role="radiogroup" aria-label="Library files" className="max-h-72 space-y-2 overflow-y-auto pr-1 scroll-thin">
                  {usable.map((f) => {
                    const active = f.id === fileId;
                    return (
                      <label
                        key={f.id}
                        className={clsx(
                          'flex min-h-11 cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors',
                          active ? 'border-primary/50 bg-primary-soft' : 'border-hairline bg-surface hover:bg-raised',
                        )}
                      >
                        <input type="radio" name="resolve-file" className="h-4 w-4 accent-[rgb(var(--primary))]" checked={active} onChange={() => setFileId(f.id)} />
                        <span className={clsx('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', active ? 'bg-surface text-primary-ink' : 'bg-raised text-muted')}>{fileIcon(f.mimeType)}</span>
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-center gap-1.5">
                            <span className="truncate font-medium text-ink">{f.friendlyName}</span>
                            <Badge>v{f.version}</Badge>
                            {f.sensitive && (
                              <Badge tone="warn" icon={<Lock className="h-3 w-3" />}>
                                Sensitive
                              </Badge>
                            )}
                            {f.id === request.suggestedLibraryFileId && <Badge tone="primary">Suggested</Badge>}
                          </span>
                          <span className="block truncate text-xs text-muted">
                            {f.filename} · {fileKind(f.mimeType, f.filename)} · {bytes(f.size)}
                            {f.expiresAt ? ` · expires ${shortDate(f.expiresAt)}` : ''}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
              {selected?.sensitive && (
                <Notice tone="warn" icon={<AlertTriangle className="h-4 w-4" />}>
                  This file is marked <strong>sensitive</strong>. Double-check it is the right document for this recipient before adding it.
                </Notice>
              )}
              {selected && allowed === false && (
                <Notice tone="warn" icon={<AlertTriangle className="h-4 w-4" />}>
                  {requesterEmail ? <strong>{requesterEmail}</strong> : 'The requester'} is not in this file's allowed recipients or domains. Only continue if you are sure — consider
                  updating the file's permissions in the Library.
                </Notice>
              )}
              {selected && allowed === null && <p className="text-xs text-muted">This file has no recipient restrictions set.</p>}
            </div>
          ) : (
            <div className="space-y-3">
              <input
                ref={fileRef}
                type="file"
                className="sr-only"
                aria-label="Choose a file to upload"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) upload.mutate(f);
                  e.target.value = '';
                }}
              />
              <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-dashed border-line bg-raised/50 p-4">
                <Button icon={<Upload className="h-4 w-4" />} loading={upload.isPending} onClick={() => fileRef.current?.click()} className="max-md:h-11">
                  {uploaded ? 'Choose a different file' : 'Choose file'}
                </Button>
                {upload.isPending && (
                  <span className="flex items-center gap-2 text-[0.8125rem] text-muted">
                    <Spinner className="h-4 w-4" label="Uploading" /> Uploading and scanning…
                  </span>
                )}
                {uploaded && !upload.isPending && (
                  <span className="min-w-0 text-[0.8125rem] text-ink">
                    <span className="font-medium">{uploaded.filename}</span>
                    <span className="text-muted"> · {bytes(uploaded.size)}</span>
                  </span>
                )}
              </div>
              <p className="text-xs text-muted">Maximum {bytes(MAX_UPLOAD_BYTES)}. Uploads are scanned before they can be sent.</p>
              {upload.isError && <InlineError error={upload.error} />}
            </div>
          )}

          <div className="space-y-2">
            <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">Deliver as</p>
            {sendAsLinks ? (
              <Segmented<Delivery>
                label="Delivery method"
                value={deliverAs}
                onChange={setDeliverAs}
                options={[
                  {
                    value: 'link',
                    label: (
                      <span className="inline-flex items-center gap-1.5">
                        <Link2 className="h-3.5 w-3.5" aria-hidden /> Protected link
                      </span>
                    ),
                  },
                  {
                    value: 'attachment',
                    label: (
                      <span className="inline-flex items-center gap-1.5">
                        <Paperclip className="h-3.5 w-3.5" aria-hidden /> Attachment
                      </span>
                    ),
                  },
                ]}
              />
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <Badge icon={<Paperclip className="h-3 w-3" />}>Attachment</Badge>
                <span className="text-xs text-muted">
                  Protected links are off. Turn on “Send approved files as links” in the{' '}
                  <Link to="/library" onClick={onClose} className="font-semibold text-primary-ink hover:underline">
                    Library
                  </Link>{' '}
                  to share by link instead.
                </span>
              </div>
            )}
            {sendAsLinks && deliverAs === 'link' && (
              <p className="text-xs text-muted">The recipient gets an expiring, revocable link with access records — never a permanent public URL.</p>
            )}
          </div>
          {resolve.isError && <InlineError error={resolve.error} />}
        </div>
      )}
    </Modal>
  );
}
